import React, { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface TooltipProps {
  content: React.ReactNode;
  children: React.ReactElement;
  side?: 'top' | 'bottom';
}

/**
 * Hover/focus tooltip rendered through a portal to <body> with position:fixed.
 * Why a portal: this page's cards use `overflow-hidden` (for rounded corners),
 * which clips any absolutely-positioned popup inside them — long help texts
 * became unreadable. Fixed positioning also lets us clamp the popup inside
 * the viewport and flip it below the trigger when there's no room above.
 */
export default function Tooltip({ content, children, side = 'top' }: TooltipProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLSpanElement | null>(null);
  const popRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number; flip: boolean } | null>(null);

  useLayoutEffect(() => {
    if (!open || !content) return;
    const place = () => {
      const trigger = triggerRef.current;
      const pop = popRef.current;
      if (!trigger) return;
      const r = trigger.getBoundingClientRect();
      // The popup sizes itself to content (capped below); measure the real box
      // when available so centering and flipping are accurate.
      const w = pop?.offsetWidth || 288;
      const h = pop?.offsetHeight || 80;
      const margin = 8;
      // Center on the trigger, then clamp so the popup stays on screen.
      const left = Math.max(margin, Math.min(r.left + r.width / 2 - w / 2, window.innerWidth - w - margin));
      // Prefer the requested side; flip to the other one when it wouldn't fit.
      const above = side === 'top';
      const fitsAbove = r.top - h - margin >= margin;
      const fitsBelow = window.innerHeight - r.bottom - h - margin >= margin;
      const showAbove = above ? (fitsAbove || !fitsBelow) : (!fitsBelow && fitsAbove);
      // showAbove → hang the popup's bottom edge `margin` above the trigger;
      // otherwise drop its top edge `margin` below the trigger.
      const top = showAbove ? r.top - margin : r.bottom + margin;
      setPos({ top, left, flip: showAbove });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, side, content]);

  if (!content) return children;
  return (
    <span
      ref={triggerRef}
      className="group/tooltip inline-flex"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
    >
      {children}
      {open &&
        createPortal(
          <div
            ref={popRef}
            role="tooltip"
            style={{
              position: 'fixed',
              top: pos ? pos.top : -9999,
              left: pos ? pos.left : -9999,
              width: 'max-content',
              minWidth: '12rem',
              maxWidth: 'min(20rem, calc(100vw - 16px))',
              visibility: pos ? 'visible' : 'hidden',
              // When shown above, anchor the popup's bottom edge near the
              // trigger instead of its top edge (translated after measurement).
              transform: pos?.flip ? 'translateY(-100%)' : undefined,
            }}
            className="z-[60] pointer-events-none px-3 py-2 rounded-xl shadow-md border
              bg-muted text-primary border-border text-sm leading-snug"
          >
            {content}
          </div>,
          document.body
        )}
    </span>
  );
}
